import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { post, put, patch } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import CustomSelector from "../../../components/AuthenticationComponents/CustomSelector";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * AdminStaffCreatePage — create/edit form for a single staff member.
 *
 * `staffId` is a URL route param (":staffId/edit"), not a prop — it must
 * be read with useParams(). It was previously destructured as a prop with
 * a `null` default, which the router never actually supplies, so every
 * `mode === "edit" && staffId` check silently evaluated to false: the
 * fetch-and-populate effect never ran, and the update PUT request would
 * have gone to ".../staff/null".
 *
 * Edit mode now uses the staff data passed via navigation state from the
 * list page to avoid unnecessary API calls. The list page provides complete
 * data including split firstName/lastName fields.
 * ---------------------------------------------------------------------- */

const AdminStaffCreatePage = ({ mode = "create", user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { staffId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const restaurantId = location.state?.restaurantId || searchParams.get('restaurant_id');
  const updatePasswordMode = location.state?.updatePasswordMode
  document.title = updatePasswordMode ? "Update Password | Omaha Food Lovers" : mode === "create" ? "Create Staff | Omaha Food Lovers" : "Edit Staff | Omaha Food Lovers";
  const [loading, setLoading] = useState(false);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Populate form from navigation state for edit mode
  useEffect(() => {
    if (mode === "edit" && staffId) {
      const stateStaffData = location.state?.staffData;
      if (stateStaffData) {
        // Use the data passed from the list page
        formik.setValues({
          first_name: stateStaffData.firstName || "",
          last_name: stateStaffData.lastName || "",
          email: stateStaffData.email || "",
          phone: stateStaffData.mobile || "",
          designation_type: stateStaffData.designation || "staff",
          password: "",
          password_confirmation: "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, staffId]);

  const validationSchema = Yup.object().shape({
    first_name: updatePasswordMode ? Yup.string() : Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must not exceed 50 characters"),
    last_name: updatePasswordMode ? Yup.string() : Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must not exceed 50 characters"),
    email: updatePasswordMode ? Yup.string().email("Please enter a valid email address") : Yup.string().required("Email is required").email("Please enter a valid email address"),
    phone: updatePasswordMode ? Yup.string() : Yup.string().required("Phone is required"),
    designation_type: updatePasswordMode ? Yup.string() : Yup.string().required("Designation is required"),
    password:
      mode === "create" || updatePasswordMode
        ? Yup.string()
            .required("Password is required")
            .min(8, "Password must be at least 8 characters")
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
              "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
            )
        : Yup.string(),
    password_confirmation: Yup.string().test("password-match", "Passwords must match", function (value) {
      const { password } = this.parent;
      if (!password || password.length === 0) return true;
      return value === password;
    }),
  });

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      designation_type: "staff",
      password: "",
      password_confirmation: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setTouched, validateForm }) => {
      setTouched({
        first_name: true,
        last_name: true,
        email: true,
        phone: true,
        designation_type: true,
        password: true,
        password_confirmation: true,
      });

      const errors = await validateForm();
      if (Object.keys(errors).length > 0) return;

      setLoading(true);

      try {
        const payload = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone,
          designation_type: values.designation_type,
        };

        if (values.password) {
          payload.password = values.password;
          payload.password_confirmation = values.password_confirmation;
        }

        if (restaurantId) {
          payload.restaurant_id = restaurantId;
        }

        let response;
        if (mode === "create") {
          response = await post(endpoints.adminRestaurantsStaffCreate, payload);
        } else if (updatePasswordMode && staffId && restaurantId) {
          // Update password only
          const passwordEndpoint = endpoints.adminRestaurantsStaffUpdatePassword
            .replace("{{restaurant_id}}", restaurantId)
            .replace("{{staff_user_id}}", staffId);
          response = await patch(passwordEndpoint, {
            password: values.password,
            password_confirmation: values.password_confirmation,
          });
        } else if (mode === "edit" && staffId && restaurantId) {
          // Update staff details
          const updateEndpoint = endpoints.adminRestaurantsStaffUpdate
            .replace("{{restaurant_id}}", restaurantId)
            .replace("{{staff_user_id}}", staffId);
          response = await put(updateEndpoint, {
            first_name: values.first_name,
            last_name: values.last_name,
            mobile: values.phone,
            designation_type: values.designation_type,
          });
        }

        // Check for different response structures
        const isSuccess = response?.data?.success || response?.data?.data?.success || response?.status === 200 || response?.status === 201;

        if (isSuccess) {
          toastr.success(
            updatePasswordMode ? "Password updated successfully" : mode === "create" ? "Staff member created successfully" : "Staff member updated successfully"
          );
          const url = restaurantId ? `/admin/staff?restaurant_id=${restaurantId}` : "/admin/staff";
          navigate(url, { state: { restaurantId } });
        } else {
          toastr.error("Failed to save staff member");
        }
      } catch (err) {
        if (err.response?.data?.error) {
          const errorObj = err.response.data.error;
          const validationErrors = Object.values(errorObj)
            .filter((value) => Array.isArray(value))
            .flat();
          toastr.error(validationErrors.length > 0 ? validationErrors.join(", ") : extractErrorMessage(err));
        } else if (err.response?.data?.errors) {
          const validationErrors = Object.values(err.response.data.errors)
            .filter((value) => Array.isArray(value))
            .flat();
          toastr.error(validationErrors.length > 0 ? validationErrors.join(", ") : extractErrorMessage(err));
        } else {
          toastr.error(extractErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div className="rp-page-header">
            <h2 className="rp-page-header__title">
              {updatePasswordMode ? "Update Staff Password" : mode === "create" ? "Create Staff Member" : "Edit Staff Member"}
            </h2>
            <p className="rp-page-header__subtitle">
              {updatePasswordMode ? "Update the staff member's password" : mode === "create" ? "Add a new staff member to your restaurant" : "Update staff member information"}
            </p>
          </div>

          <Form onSubmit={formik.handleSubmit}>
            {!updatePasswordMode && (
              <>
                <Row>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="first_name"
                      label="First Name"
                      name="first_name"
                      type="text"
                      placeholder="Enter first name"
                      value={formik.values.first_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.first_name && !!formik.errors.first_name}
                      errorText={formik.errors.first_name}
                      className="white-bg"
                    />
                  </Col>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="last_name"
                      label="Last Name"
                      name="last_name"
                      type="text"
                      placeholder="Enter last name"
                      value={formik.values.last_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.last_name && !!formik.errors.last_name}
                      errorText={formik.errors.last_name}
                      className="white-bg"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col sm={12}>
                    <CustomInput
                      id="email"
                      label="Email"
                      name="email"
                      type="email"
                      placeholder="Enter email address"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.email && !!formik.errors.email}
                      errorText={formik.errors.email}
                      className="white-bg"
                      disabled={mode === "edit"}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="phone"
                      label="Phone"
                      name="phone"
                      type="text"
                      placeholder="Enter phone number"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.phone && !!formik.errors.phone}
                      errorText={formik.errors.phone}
                      className="white-bg"
                    />
                  </Col>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomSelector
                      id="designation_type"
                      label="Designation"
                      name="designation_type"
                      placeholder="Select designation"
                      options={[
                        { value: "admin", label: "Admin" },
                        { value: "manager", label: "Manager" },
                        { value: "staff", label: "Staff" }
                      ]}
                      value={formik.values.designation_type}
                      onChange={(value) => formik.setFieldValue("designation_type", value)}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.designation_type && !!formik.errors.designation_type}
                      errorText={formik.errors.designation_type}
                      className="white-bg"
                    />
                  </Col>
                </Row>
              </>
            )}

            {(mode === "create" || updatePasswordMode) && (
              <Row>
                <Col xl={6} lg={6} md={12} sm={12}>
                  <CustomInput
                    id="password"
                    label={updatePasswordMode ? "New Password" : "Password"}
                    name="password"
                    type="password"
                    placeholder="Enter password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.password && !!formik.errors.password}
                    errorText={formik.errors.password}
                    className="white-bg"
                  />
                </Col>
                <Col xl={6} lg={6} md={12} sm={12}>
                  <CustomInput
                    id="password_confirmation"
                    label={updatePasswordMode ? "Confirm New Password" : "Confirm Password"}
                    name="password_confirmation"
                    type="password"
                    placeholder="Confirm password"
                    value={formik.values.password_confirmation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.password_confirmation && !!formik.errors.password_confirmation
                    }
                    errorText={formik.errors.password_confirmation}
                    className="white-bg"
                  />
                </Col>
              </Row>
            )}

              <div className="mt-2">
                <SecondaryButton
                  type="submit"
                  variant="orange"
                  disabled={loading}
                  loading={loading}
                  icon={tickIcon}
                  iconPosition="right"
                >
                  {loading ? "Saving..." : updatePasswordMode ? "Update Password" : mode === "create" ? "Create Staff Member" : "Update Staff Member"}
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  variant="light-grey"
                  onClick={() => {
                  const url = restaurantId ? `/admin/staff?restaurant_id=${restaurantId}` : "/admin/staff";
                  navigate(url, { state: { restaurantId } });
                }}
                  icon={cancelIcon}
                  iconPosition="right"
                  style={{ marginLeft: "10px" }}
                >
                  Cancel
                </SecondaryButton>
              </div>
            </Form>
        </div>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.Login.user,
});

export default connect(mapStateToProps, null)(AdminStaffCreatePage);
